import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { useAccount } from 'wagmi';
import { SignatureContext } from './SignatureContext';

interface AdminContextType {
  isAdmin: boolean | null;
  adminRole: string | null;
  verifyAdmin: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: null,
  adminRole: null,
  verifyAdmin: async () => {},
});

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [adminRole, setAdminRole] = useState<string | null>(null);
  const { address, isConnected } = useAccount();
  const { signature } = useContext(SignatureContext);

  const verifyAdmin = async () => {
    if (!address || !signature) { return; }

    try {
      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${signature}`,
        },
        body: JSON.stringify({ address }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsAdmin(data.isPrivileged);
        setAdminRole(data.role);
        if (data.isPrivileged) {
          toast.success(`You have ${data.role} privileges!`);
        }
      } else {
        setIsAdmin(false);
        setAdminRole(null);
        toast.error(data.error || 'Failed to verify privileges');
      }
    } catch (err: any) {
      console.error('Error verifying privileges:', err);
      toast.error('Failed to verify privileges. Please try again.');
      setIsAdmin(false);
      setAdminRole(null);
    }
  };

  useEffect(() => {
    if (isConnected && signature) {
      verifyAdmin();
    } else {
      setIsAdmin(null);
      setAdminRole(null);
    }
  }, [isConnected, signature]);

  return (
    <AdminContext.Provider value={{ isAdmin, adminRole, verifyAdmin }}>
      {children}
    </AdminContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminContext);
